import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { MyInfoState } from '@/recoil/atom';
import { useDialog } from '@/hooks/global-tools';
import { isRnWebView } from '@/utils/function';
import { isAttendanceApprovalCompanyAllowed } from '@/components/Approval/CreateApprovalModal/attendanceApproval';
import WorkingHoursChangeModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/WorkingHoursChangeModal';
import OvertimeRequestModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeRequestModal';
import OvertimeAdjustModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeAdjustModal';
import AnnualLeaveModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/AnnualLeaveModal';

type Props = {
  /** apv_type — 222 / 223 / 224 / 231 */
  readonly type: number;
};

/**
 * 근태 결재 양식(222/223/224/231)만 단독으로 띄우는 화면.
 *
 * 앱의 네이티브 결재 탭이 `/m/approval?write=<apv_type>` 로 이 화면을 열어 웹 모달을
 * 그대로 재사용한다. 결재 목록(MobileApproval)은 렌더하지 않는다 — 네이티브 결재 탭
 * 위에 웹 결재 목록이 한 겹 더 쌓이면 같은 화면이 두 번 보인다.
 *
 * 모달을 닫으면 앱에서는 네이티브 화면을 pop 하고, 웹 브라우저에서는 결재 목록으로 간다.
 */
const AttendanceApprovalForm = ({ type }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dialog = useDialog();
  const myInfo = useAtomValue(MyInfoState);
  const { companyCode, workingHours } = myInfo;

  // 모달은 마운트와 함께 열린 상태로 시작한다. (닫으면 이 화면을 벗어난다)
  const [open, setOpen] = useState(true);

  const close = () => {
    setOpen(false);
    if (isRnWebView()) {
      window?.ReactNativeWebView?.postMessage(
        JSON.stringify({ actionType: 'BACK' }),
      );
      return;
    }
    router.replace({ pathname: router.pathname });
  };

  // 미허용 법인은 양식 진입 차단 (데스크탑/모바일 결재 작성과 동일 규칙)
  const isAllowed = isAttendanceApprovalCompanyAllowed(type, companyCode);
  useEffect(() => {
    // companyCode 가 아직 안 채워졌으면(초기화 전) 판단을 미룬다
    if (!companyCode || isAllowed) return;
    // 문구는 결재 작성(ApprovalWirte)과 동일하게 맞춘다
    dialog.show({
      message: t('해당 양식을 지원하지 않는 법인입니다.'),
      onConfirm: close,
    });
  }, [companyCode, isAllowed]);

  if (!companyCode || !isAllowed) {
    return null;
  }

  switch (type) {
    case 222:
      return (
        <WorkingHoursChangeModal
          open={open}
          onClose={close}
          myInfo={myInfo}
          menuOnClose={close}
        />
      );
    case 223:
      return (
        <OvertimeRequestModal
          open={open}
          onClose={close}
          workingHours={workingHours ?? undefined}
        />
      );
    case 224:
      return (
        <OvertimeAdjustModal
          open={open}
          onClose={close}
          workingHours={workingHours ?? undefined}
        />
      );
    case 231:
      return <AnnualLeaveModal open={open} onClose={close} />;
    default:
      return null;
  }
};

export default AttendanceApprovalForm;
